<script setup lang="ts">
  import SettingsPage from '@/components/app/settings/SettingsPage.vue'
  import CheckboxField from '@/components/admin/form/CheckboxField.vue'
  import { useTranslation } from '@/composables/useTranslation'
  import { computed, onMounted, ref, watch } from 'vue'
  import { ComputerDesktopIcon } from '@heroicons/vue/24/outline'
  import { useUserStore } from '@/shared/stores/userStore'
  import type { CustomerUser } from '@/types/generated'
  import { OrganisationsService } from '@/types/generated'
  import * as Sentry from '@sentry/vue'

  const { t } = useTranslation({
    en: {
      enableDatev: 'Enable DATEV Integration',
      integrationEnabled: 'DATEV Integration Enabled',
      integrationDescription:
        'You can now export cases to DATEV Anwalt Inkasso-Import format. The export will generate a ZIP file containing an XML file and all selected documents.',
      howToUse: 'How to use DATEV Export:',
      step1: 'Go to a case detail page',
      step2: 'Click on "Export" in the case actions',
      step3: 'Select "DATEV" as the export format',
      step4: 'Choose your export options and download the ZIP file'
    },
    de: {
      enableDatev: 'DATEV Integration aktivieren',
      integrationEnabled: 'DATEV Integration aktiviert',
      integrationDescription:
        'Sie können jetzt Anfragen in das DATEV Anwalt Inkasso-Import Format exportieren. Der Export erstellt eine ZIP-Datei mit einer XML-Datei und allen ausgewählten Dokumenten.',
      howToUse: 'So verwenden Sie den DATEV Export:',
      step1: 'Gehen Sie zu einer Anfrage in JUPUS',
      step2: 'Klicken Sie auf "Export"',
      step3: 'Wählen Sie "DATEV" als Exportformat',
      step4: 'Wählen Sie Ihre Exportoptionen und laden Sie die ZIP-Datei herunter'
    }
  })
  const isLoading = ref(false)
  const { fetchUser } = useUserStore()
  const user = computed<CustomerUser>(() => useUserStore().user)

  // Form state
  const isDatevEnabled = ref(false)

  // Initialize form values from user data
  onMounted(() => {
    if (user.value?.org) {
      isDatevEnabled.value = user.value.org.is_datev_export_integration_enabled || false
    }
  })

  // Watch for changes and auto-save
  watch(isDatevEnabled, async (newValue) => {
    if (user.value?.org && newValue !== user.value.org.is_datev_export_integration_enabled) {
      await saveSettings()
    }
  })

  const saveSettings = async () => {
    if (!user.value?.org || isLoading.value) return

    isLoading.value = true
    try {
      const data = {
        is_datev_export_integration_enabled: isDatevEnabled.value
      }

      await updateOrganisation(data)
      await fetchUser() // Refresh user data
    } catch (error) {
      Sentry.captureException(error)
      // Revert the checkbox state on error
      isDatevEnabled.value = user.value.org.is_datev_export_integration_enabled || false
    } finally {
      isLoading.value = false
    }
  }

  const updateOrganisation = async (data: any) => {
    try {
      await OrganisationsService.organisationsPartialUpdate({
        uuid: user.value!.org.uuid,
        requestBody: data
      })
    } catch (error) {
      Sentry.captureException(error)
      throw error
    }
  }
</script>

<template>
  <SettingsPage v-if="user">
    <div class="space-y-8">
      <div class="mb-6">
        <!-- DATEV Integration Settings -->
        <div class="max-w-xl space-y-4">
          <!-- Enable DATEV Integration -->
          <CheckboxField
            v-model="isDatevEnabled"
            name="is_datev_export_integration_enabled"
            :label="t('enableDatev')"
            :disabled="isLoading"
          />

          <!-- Information about DATEV -->
          <div
            v-if="isDatevEnabled"
            class="mt-4 rounded-lg bg-blue-50 p-4"
          >
            <div class="flex">
              <div class="flex-shrink-0">
                <ComputerDesktopIcon
                  class="h-5 w-5 text-blue-400"
                  aria-hidden="true"
                />
              </div>
              <div class="ml-3">
                <h3 class="text-sm font-medium text-blue-800">{{ t('integrationEnabled') }}</h3>
                <div class="mt-2 text-sm text-blue-700">
                  <p>{{ t('integrationDescription') }}</p>
                </div>
              </div>
            </div>
          </div>

          <!-- Usage Instructions -->
          <div class="mt-6 rounded-lg bg-gray-50 p-4">
            <h4 class="mb-2 text-sm font-medium text-gray-900">{{ t('howToUse') }}</h4>
            <ol class="list-inside list-decimal space-y-1 text-sm text-gray-700">
              <li>{{ t('step1') }}</li>
              <li>{{ t('step2') }}</li>
              <li>{{ t('step3') }}</li>
              <li>{{ t('step4') }}</li>
            </ol>
          </div>
        </div>
      </div>
    </div>
  </SettingsPage>
</template>

<style scoped>
  /* Add any scoped styles here if needed */
</style>
